import stopwordsData from './lexical/stopwords.json';
import synonymsData from './lexical/synonyms.json';
import { findMatchingIntent, getFeatureKeywordsFromIntent } from './intent';

export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .trim();
}

const STOP_WORDS = new Set(stopwordsData);
const SYNONYMS: Record<string, string[]> = synonymsData;

export function extractTerms(query: string): string[] {
  return normalize(query)
    .split(' ')
    .filter((word) => word.length > 2 && !STOP_WORDS.has(word));
}

/**
 * Simple pluralization handler - converts plural to singular and vice versa
 */
function getPluralForms(word: string): string[] {
  const forms: string[] = [word];

  if (word.endsWith('es') && word.length > 3) {
    const beforeEs = word.slice(0, -2);
    const lastChar = beforeEs[beforeEs.length - 1];
    if (['s', 'x', 'z', 'h'].includes(lastChar)) {
      if (beforeEs.length > 2) {
        forms.push(beforeEs);
      }
      return Array.from(new Set(forms));
    }
  }

  if (word.endsWith('s') && word.length > 1) {
    const singular = word.slice(0, -1);
    if (singular.length > 2) {
      forms.push(singular);
    }
  } else if (word.length > 2) {
    forms.push(word + 's');
  }

  return Array.from(new Set(forms));
}

export function expandTerms(terms: string[], originalQuery?: string): string[] {
  const expanded: string[] = [];
  let featureKeywords: string[] = [];

  if (originalQuery) {
    const intentMatch = findMatchingIntent(originalQuery);
    if (intentMatch) {
      featureKeywords = getFeatureKeywordsFromIntent(intentMatch.intent);
      for (const keyword of featureKeywords) {
        expanded.push(keyword);
        expanded.push(...getPluralForms(keyword));
      }
    }
  }

  for (const term of terms) {
    expanded.push(term);

    if (SYNONYMS[term]) {
      expanded.push(...SYNONYMS[term]);
    }

    expanded.push(...getPluralForms(term));

    if (SYNONYMS[term]) {
      for (const synonym of SYNONYMS[term]) {
        expanded.push(...getPluralForms(synonym));
      }
    }
  }

  return Array.from(new Set(expanded));
}

export function fuzzyMatch(text: string, term: string): boolean {
  const normalizedText = normalize(text);
  const normalizedTerm = normalize(term);
  return normalizedText.includes(normalizedTerm);
}

export function scoreDocument(
  docText: string,
  expandedTerms: string[],
  featureKeywords: string[] = []
): number {
  const normalizedDoc = normalize(docText);
  let score = 0;
  const normalizedFeatureKeywords = new Set(
    featureKeywords.map((keyword) => normalize(keyword))
  );

  for (const term of expandedTerms) {
    const normalizedTerm = normalize(term);
    if (normalizedDoc.includes(normalizedTerm)) {
      if (normalizedFeatureKeywords.has(normalizedTerm)) {
        score += 10;
      } else {
        score += term.length > 0 ? 1 : 0.5;
      }
    }
  }

  return score;
}

export function fuzzySearch<T extends { text?: string; [key: string]: any }>(
  docs: T[],
  query: string,
  textExtractor?: (doc: T) => string
): Array<{ doc: T; score: number }> {
  const terms = extractTerms(query);
  if (terms.length === 0) {
    return [];
  }

  const expanded = expandTerms(terms, query);

  let featureKeywords: string[] = [];
  const intentMatch = findMatchingIntent(query);
  if (intentMatch) {
    featureKeywords = getFeatureKeywordsFromIntent(intentMatch.intent);
  }

  return docs
    .map((doc) => {
      const docText = textExtractor ? textExtractor(doc) : doc.text || '';
      return {
        doc,
        score: scoreDocument(docText, expanded, featureKeywords),
      };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
}

export function matchesQueryFuzzy(
  text: string | null | undefined,
  query: string
): boolean {
  if (!text) return false;

  const terms = extractTerms(query);
  if (terms.length === 0) return false;

  const normalizedText = normalize(text);
  const originalTerms = new Set(terms);

  const intentMatch = findMatchingIntent(query);
  let featureKeywords: string[] = [];
  if (intentMatch) {
    featureKeywords = getFeatureKeywordsFromIntent(intentMatch.intent);

    // PRIORITY 1: Check feature keywords FIRST (before synonyms)
    for (const keyword of featureKeywords) {
      const normalizedKeyword = normalize(keyword);
      if (normalizedText.includes(normalizedKeyword)) {
        return true;
      }

      // Also check with word boundaries for multi-word keywords
      if (keyword.includes(' ')) {
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const keywordRegex = new RegExp(escapedKeyword, 'i');
        if (keywordRegex.test(text)) {
          return true;
        }
      }
    }
  }

  // PRIORITY 2: Check for exact term matches
  for (const term of terms) {
    if (fuzzyMatch(normalizedText, term)) {
      return true;
    }
  }

  // PRIORITY 3: Check expanded terms (synonyms), but use word boundaries for single words
  // to reduce false positives (e.g., "brake" in "brake pedal" when searching "brakes")
  const expanded = expandTerms(terms, query);
  for (const term of expanded) {
    if (originalTerms.has(term)) continue;
    if (
      featureKeywords.some((keyword) => normalize(keyword) === normalize(term))
    )
      continue;

    if (!term.includes(' ')) {
      // For single words, use word boundary matching to avoid substring false positives
      // This helps prevent "brake pedal" from matching when searching for "brakes" (parking brakes)
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const wordBoundaryRegex = new RegExp(`\\b${escapedTerm}\\b`, 'i');
      if (wordBoundaryRegex.test(normalizedText)) {
        return true;
      }
    } else {
      // For multi-word terms (like "parking brake"), use substring matching
      if (fuzzyMatch(normalizedText, term)) {
        return true;
      }
    }
  }

  return false;
}
